import React, { useEffect, useRef, useState } from 'react';
import { format } from 'timeago.js';
import { PUBLIC_PATH } from '../../../../../shared/config/routes-config';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { User } from '../../../../../ui/Icons';

interface Comment {
  id: string;
  email: string;
  name: string;
  message: string;
  createdAt: Date;
}

export interface CommentsModalProps {
  config: {
    showModal: boolean;
    isSubmitting?: boolean;
  };
  data: {
    commentsList: Comment[];
  };
  eventHandlers: {
    onClose: () => void;
    onSubmit: (value: string) => void;
  };
}

export const CommentsModal: React.FC<CommentsModalProps> = ({
  config: { showModal, isSubmitting },
  data: { commentsList },
  eventHandlers: { onClose, onSubmit }
}) => {
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showModal && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showModal]);

  console.log(commentsList);

  const handleSubmit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    setDraft('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  return (
    <Modal
      config={{ showModal, fullScreen: false, showClose: true, maxWidth: 'sm' }}
      eventHandlers={{ closeModalHandler: onClose }}
    >
      <div className="flex flex-col h-[75vh] max-h-[640px]">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <h2 className="text-[1.8rem] font-semibold text-gray-800">Comments</h2>
          <p className="text-[1.2rem] text-gray-400 mt-0.5">
            {commentsList.length} message{commentsList.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-gray-50">
          {commentsList.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
              <img
                src={`${PUBLIC_PATH}/assets/svgs/no-data.svg`}
                alt="no messages"
                className="w-24 opacity-50"
              />
              <span className="text-[1.3rem]">No messages yet</span>
            </div>
          ) : (
            commentsList.map((comment) => (
              <div key={comment.id} className="flex gap-3 items-start">
                {/* <img
                  src={`${PUBLIC_PATH}/assets/pngs/male-avatar.png`}
                  alt={comment.name || comment.email || 'user'}
                  className="w-[3.6rem] h-[3.6rem] rounded-full object-cover flex-shrink-0 border border-gray-200"
                /> */}
                <User />
                <div className="flex-1 min-w-0">
                  <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm border border-gray-100">
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="text-2xl font-bold font-gilroy-medium text-gray-800 truncate capitalize">
                        {comment.name || comment.email}
                      </span>
                      <span className="text-[1.1rem] text-gray-400 flex-shrink-0">
                        {format(comment.createdAt)}
                      </span>
                    </div>
                    <p className="text-xl text-neutral-300 font-gilroy-medium leading-relaxed whitespace-pre-wrap break-words">
                      {comment.message}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        {/* Compose */}
        <div className="px-6 pb-6 pt-4 border-t border-gray-100 bg-white">
          <div className="flex gap-3 items-center">
            <textarea
              className="flex-1 resize-none rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-[1.35rem] text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors min-h-[5rem] max-h-[12rem]"
              placeholder="Write a message… (Ctrl+Enter to send)"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
            />
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!draft.trim() || isSubmitting}
              className="flex-shrink-0 w-[4.4rem] h-[4.4rem] rounded-xl bg-primary-500 text-white flex items-center justify-center hover:bg-primary-700 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
              title="Send (Ctrl+Enter)"
            >
              {isSubmitting ? (
                <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <title>Sending</title>
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 translate-x-0.5">
                  <title>Send</title>
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              )}
            </button>
          </div>
          <p className="text-[1.1rem] text-gray-400 mt-2">Ctrl+Enter to send</p>
        </div>
      </div>
    </Modal>
  );
};
